import { useCallback, useEffect, useMemo, useState } from "react";

import { useMsal } from "@azure/msal-react";
import { useIsMutating } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Merge } from "lucide-react";
import { useDispatch } from "react-redux";

import { Card } from "@/components/ui/card";
import { MUTATION_KEYS } from "@/constants/clientKeys";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { getFileUrl } from "@/utils/file";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";
import type { GetAllFilesApiV1FilesGetData } from "@api/generated/types.gen";

import { DeleteMultipleFilesConfirmation } from "@components/FilesListDataTable";
import { ResizablePanels } from "@components/ResizablePanels";
import { Button } from "@components/ui/button";

import { useMergeFiles, useUnmergeFile } from "@hooks/mutations/use-file-merge";
import { useFiles } from "@hooks/queries/use-files";

import { useCaseDetail } from "@pages/ReportStudio/CaseDetail/context";

import { setMessage } from "@redux/notification";

import { DocumentChronologicalView } from "./components/DocumentChronologicalView";
import { DocumentPreview } from "./components/DocumentPreview";
import { DocumentTimelineView } from "./components/DocumentTimelineView";
import { DocumentTypeView } from "./components/DocumentTypeView";
import { FileFilter } from "./components/FileFilter";
import { MergeDocumentsModal } from "./components/MergeDocumentsModal";
import { SearchTypeSelector } from "./components/SearchTypeSelector";
import { SearchType } from "./components/SearchTypeSelector";
import { SemanticSearchInput } from "./components/SemanticSearchInput";
import { SummaryPanel } from "./components/SummaryPanel";
import { UploadBundleFile } from "./components/UploadBundleFile";
import { UploadDocumentsModal } from "./components/UploadDocumentsModal";

const DocumentSorterPage = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const dispatch = useDispatch();
  const { setRightContent } = useCaseDetail();

  const isDeleting =
    useIsMutating({ mutationKey: MUTATION_KEYS.BULK_REMOVE_FILES }) > 0;

  const [searchType, setSearchType] = useState<SearchType>("document_type");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [filters, setFilters] = useState<GetAllFilesApiV1FilesGetData["query"]>({
    case_number: caseNumber,
    status: undefined,
    classification: undefined,
    created_at: undefined,
    issue_date: undefined,
    sort_by: "recently_added",
  });

  const [selectedDocument, setSelectedDocument] =
    useState<DocSorterFileSchema | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const { accounts } = useMsal();
  const [showSummaryPanel, setShowSummaryPanel] = useState(false);
  const [isUploadBundleFileOpen, setIsUploadBundleFileOpen] = useState(false);
  const [isUploadDocumentsModalOpen, setIsUploadDocumentsModalOpen] =
    useState(false);
  const [isMergeMode] = useState(true);
  const [mergeSelectedFiles, setMergeSelectedFiles] = useState<string[]>([]);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  const { data: caseData, isLoading: isCaseLoading } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const {
    data: files = [],
    isLoading,
    refetch: refetchFiles,
  } = useFiles({
    case_number: caseNumber || "",
    sort_by: searchType === "chronological" ? "recently_added" : undefined,
    classification: filters?.classification,
    status: filters?.status,
    created_at: filters?.created_at,
  });

  // Merge and unmerge hooks
  const { mutate: mergeFiles, isPending: isMerging } = useMergeFiles();
  const { mutate: unmergeFile, isPending: isUnmerging } = useUnmergeFile();

  const handleOpenMergeModal = useCallback(() => {
    if (mergeSelectedFiles.length >= 2) {
      setIsMergeModalOpen(true);
    }
  }, [mergeSelectedFiles.length]);

  // Set page header actions
  useEffect(() => {
    setRightContent(
      <div className="flex gap-2">
        <UploadBundleFile
          caseNumber={caseNumber}
          onToastOpen={() => setIsUploadBundleFileOpen(true)}
          onToastClose={() => setIsUploadBundleFileOpen(false)}
        />
        <UploadDocumentsModal
          caseNumber={caseNumber}
          onModalOpen={() => setIsUploadDocumentsModalOpen(true)}
          onModalClose={() => setIsUploadDocumentsModalOpen(false)}
        />
      </div>
    );

    return () => {
      setRightContent(null);
    };
  }, [caseNumber, setRightContent]);

  const organizedFiles = useMemo(() => {
    if (searchType === "chronological") {
      return [...files].sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
    } else if (searchType === "document_type") {
      const groupedByType: Record<string, DocSorterFileSchema[]> = {};

      files.forEach((file) => {
        const type = file.classification || "Uncategorized";
        if (!groupedByType[type]) {
          groupedByType[type] = [];
        }
        groupedByType[type].push(file);
      });

      // Sort groups and files within each group by document date
      const sortedGroupKeys = Object.keys(groupedByType).sort();
      return sortedGroupKeys.flatMap((key) =>
        groupedByType[key].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA; // Most recent first
        })
      );
    } else if (searchType === "timeline") {
      // Timeline view doesn't need special organization, it's handled in the DocumentTimelineView component
      return files;
    } else {
      // Default view (FilesListWithDragDrop) - return files as is
      return files;
    }
  }, [files, searchType]);

  // Memoize the file object to prevent unnecessary re-renders
  const documentPreviewFile = useMemo(() => {
    if (!selectedDocument) return null;
    return {
      id: selectedDocument.id || "",
      name: selectedDocument.name || "",
      classification: selectedDocument.classification || "",
      date: selectedDocument.date,
      case: selectedDocument.case || { id: "", case_number: "" },
      full_summary: selectedDocument.full_summary || "",
    };
  }, [selectedDocument]);

  // Fetch fileUrl and fileType when selectedDocument changes
  useEffect(() => {
    let cancelled = false;
    async function fetchFileUrl() {
      if (!selectedDocument) {
        setFileUrl(null);
        setFileType(null);
        return;
      }
      try {
        const result = await getFileUrl(selectedDocument);
        if (!cancelled) {
          setFileUrl(result.url || null);
          setFileType(result.type || null);
        }
      } catch (e) {
        setFileUrl(null);
        setFileType(null);
      }
    }
    fetchFileUrl();
    return () => {
      cancelled = true;
    };
  }, [selectedDocument, accounts]);

  // Effect to refresh the selectedDocument when files change
  // This ensures that if a file's classification or full_summary is updated, the selectedDocument also gets updated
  useEffect(() => {
    if (!selectedDocument) return;

    // Find the currently selected document in the updated files list
    const updatedSelectedDocument = files.find(
      (file) => file.id === selectedDocument.id
    );

    // If found and classification or full_summary has changed, update the selectedDocument
    if (
      updatedSelectedDocument &&
      (updatedSelectedDocument.classification !== selectedDocument.classification ||
        updatedSelectedDocument.full_summary !== selectedDocument.full_summary)
    ) {
      setSelectedDocument(updatedSelectedDocument);
    }
  }, [files, selectedDocument]);

  if (!caseNumber) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-destructive">Invalid case</p>
      </div>
    );
  }

  const handleSelectFile = (fileId: string, selected: boolean) => {
    if (selected) {
      setSelectedFiles((prev) => [...prev, fileId]);
    } else {
      setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedFiles(organizedFiles.map((file) => file.id));
    } else {
      setSelectedFiles([]);
    }
  };

  const handleViewFile = (file: DocSorterFileSchema | null) => {
    setSelectedDocument(file);
    setShowSummaryPanel(false);
  };

  const handleViewSummary = (file: DocSorterFileSchema) => {
    setSelectedDocument(file);
    setShowSummaryPanel(true);
  };

  const handleSearchTypeChange = (type: SearchType) => {
    setSearchType(type);
    refetchFiles();
    // Reset selected document when changing search types to ensure only Panel 1 is visible
    setSelectedDocument(null);
    setShowSummaryPanel(false);
    if (type === "timeline") {
      setSelectedFiles([]);
      setMergeSelectedFiles([]);
    }
  };

  const handleFiltersChange = (
    newFilters: GetAllFilesApiV1FilesGetData["query"]
  ) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const handleSelectFileForMerge = (fileId: string, selected: boolean) => {
    if (selected) {
      setMergeSelectedFiles((prev) => [...prev, fileId]);
      setSelectedFiles((prev) => [...prev, fileId]);
    } else {
      setMergeSelectedFiles((prev) => prev.filter((id) => id !== fileId));
      setSelectedFiles((prev) => prev.filter((id) => id !== fileId));
    }
  };

  const handleSelectAllForMerge = (selected: boolean) => {
    if (selected) {
      const allIds = organizedFiles.map((file) => file.id);
      setMergeSelectedFiles(allIds);
      setSelectedFiles(allIds);
    } else {
      setMergeSelectedFiles([]);
      setSelectedFiles([]);
    }
  };

  const handleMerge = (category: string, orderedFileIds: string[]) => {
    // Show loading toast
    dispatch(
      setMessage({
        content: `Merging ${orderedFileIds.length} files`,
        type: "info",
        isLoading: true,
      })
    );

    mergeFiles(
      {
        file_ids: orderedFileIds,
        target_classification: category,
        file_order: orderedFileIds,
      },
      {
        onSuccess: (data) => {
          // Reset state
          setMergeSelectedFiles([]);
          setIsMergeModalOpen(false);

          // Show success toast with undo button
          dispatch(
            setMessage({
              content: `Successfully merged ${orderedFileIds.length} files into "${stripBundlePrefix(data.merged_file.name)}".`,
              type: "success",
              action: {
                label: "Undo",
                onClick: () =>
                  handleUndoMerge(data.merged_file.id, data.merged_file.name),
              },
            })
          );
        },
        onError: (error) => {
          dispatch(
            setMessage({
              content: `Failed to merge files: ${error.message}`,
              type: "error",
            })
          );
        },
      }
    );
  };

  const handleUndoMerge = (mergedFileId: string, mergedFileName?: string) => {
    const fileName = mergedFileName || "merged file";

    // Show loading toast
    dispatch(
      setMessage({
        content: `Unmerging "${fileName}"`,
        type: "info",
        isLoading: true,
      })
    );

    unmergeFile(mergedFileId, {
      onSuccess: () => {
        dispatch(
          setMessage({
            content: `Successfully unmerged "${fileName}". Original files restored.`,
            type: "success",
          })
        );
      },
      onError: (error) => {
        dispatch(
          setMessage({
            content: `Failed to undo merge: ${error.message}`,
            type: "error",
          })
        );
      },
    });
  };

  const renderDocumentView = () => {
    const commonProps = {
      files,
      onViewFile: handleViewFile,
      onViewSummary: handleViewSummary,
      isLoading,
      selectedDocumentId: selectedDocument?.id,
      showSummaryPanel,
      caseId: caseData?.id,
    };

    const selectionProps = {
      selectedFiles,
      onSelectFile: handleSelectFile,
      onSelectAll: handleSelectAll,
    };

    const mergeProps = {
      isMergeMode,
      mergeSelectedFiles,
      onSelectFileForMerge: handleSelectFileForMerge,
      onSelectAllForMerge: handleSelectAllForMerge,
    };

    switch (searchType) {
      case "timeline":
        return (
          <DocumentTimelineView
            {...commonProps}
            {...selectionProps}
            caseId={caseData?.id}
          />
        );

      case "chronological":
        return (
          <DocumentChronologicalView
            {...commonProps}
            {...selectionProps}
            {...mergeProps}
          />
        );

      default:
        return (
          <DocumentTypeView
            {...commonProps}
            {...mergeProps}
            onUnmergeFile={handleUndoMerge}
          />
        );
    }
  };

  const renderDocumentPreview = () => {
    if (!selectedDocument || !documentPreviewFile) return null;

    return (
      <DocumentPreview
        fileName={documentPreviewFile.name}
        fileUrl={fileUrl}
        fileType={fileType}
        file={selectedDocument}
        caseNumber={caseNumber}
        onClose={() => {
          setSelectedDocument(null);
          setShowSummaryPanel(false);
        }}
        onRetry={() => {
          const updatedFile = files.find((f) => f.id === selectedDocument.id);
          if (updatedFile) {
            setSelectedDocument(updatedFile);
          }
        }}
        isLoading={!fileUrl}
        fileLoadError={!fileUrl && !!selectedDocument}
      />
    );
  };

  const renderSummaryPanel = () => {
    if (!showSummaryPanel) return null;

    return (
      <section className="min-h-0 border-l border-border p-4">
        <SummaryPanel
          file={selectedDocument}
          isVisible={true}
          onClose={() => {
            setShowSummaryPanel(false);
          }}
        />
      </section>
    );
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setMergeSelectedFiles([]);
  };

  return (
    <div className="grid h-full grid-rows-[auto_1fr] gap-2">
      {/* Search and Filters */}
      <div>
        <div className="mb-2 flex items-center gap-4">
          <div className="flex-1">
            <SemanticSearchInput />
          </div>

          <FileFilter
            onFiltersChange={handleFiltersChange}
            isStatusEditable={true}
          />
        </div>

        <div className="mt-2">
          <SearchTypeSelector value={searchType} onChange={handleSearchTypeChange} />
        </div>

        {selectedFiles.length > 0 && (
          <div className="mt-2 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5">
            <span className="text-xs font-medium text-primary">
              {selectedFiles.length} selected
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              {mergeSelectedFiles.length >= 2 && (
                <Button
                  onClick={handleOpenMergeModal}
                  size="compact"
                  disabled={isDeleting}
                >
                  <Merge />
                  Merge
                  <span className="ml-0.5 rounded-full bg-primary-foreground/20 px-1.5 py-0.5 text-[10px] font-semibold leading-none">
                    {mergeSelectedFiles.length}
                  </span>
                </Button>
              )}
              <DeleteMultipleFilesConfirmation
                fileIds={selectedFiles}
                onDeleteSuccess={clearSelection}
                buttonSize="compact"
              />
              <Button
                variant="ghost"
                size="compact"
                onClick={clearSelection}
                disabled={isDeleting}
              >
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Files Views */}
      <Card
        className={cn(
          "min-h-0 overflow-hidden p-0",
          searchType !== null ? "overflow-hidden" : "overflow-auto"
        )}
      >
        {showSummaryPanel ? (
          // Fixed three-column layout when summary panel is open
          <div className="grid h-full grid-cols-3">
            <div
              className={cn(
                "min-h-0 min-w-0",
                searchType !== null
                  ? "overflow-auto overflow-x-auto"
                  : "overflow-visible"
              )}
            >
              {renderDocumentView()}
            </div>
            {renderDocumentPreview()}
            {renderSummaryPanel()}
          </div>
        ) : (
          // Resizable two-column layout when only document preview is open
          <ResizablePanels
            className="h-full"
            left={
              <div
                className={cn(
                  "h-full min-h-0 min-w-0",
                  searchType !== null
                    ? "overflow-auto overflow-x-auto"
                    : "overflow-visible"
                )}
              >
                {renderDocumentView()}
              </div>
            }
            right={renderDocumentPreview()}
            defaultRightWidthPercent={33}
            minRightWidthPercent={20}
            maxRightWidthPercent={65}
          />
        )}
      </Card>

      {/* Merge Documents Modal */}
      <MergeDocumentsModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        selectedFiles={files.filter((file) => mergeSelectedFiles.includes(file.id))}
        onMerge={handleMerge}
        isLoading={isMerging}
      />
    </div>
  );
};

export default DocumentSorterPage;
